var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { Interval } from '@shared/utils/History/Interval';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { Color } from '../../../../Graphics';
import { TimeInterval } from '../../../TimeInterval';
import { BarData } from '../../HistoricalData';
import { HistoricalDataRequest } from '../../HistoricalDataRequest';
import { IndicatorDataType, Period } from '../../IndicatorEnums';
import { IndicatorScriptBase } from '../../IndicatorScriptBase';
import { LineStyle } from '../../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../../InputParamaterClasses/InputParameterInteger';
import { InputParameterRangeControl } from '../../InputParamaterClasses/InputParameterRangeControl';
import { VWAPCalculator } from './VWAPCalculator';
import { VWAPHistoryStatus } from './VWAPHistoryStatus';
import { TimeSpan } from '@shared/utils/Time/TimeSpan';
import { Periods } from '@shared/utils/History/TFInfo';
export class VWAP extends IndicatorScriptBase {
    constructor() {
        super();
        this.vwapHistoryStatus = VWAPHistoryStatus.None;
        this.DaysCount = 1;
        this.TimeInterval = new TimeInterval();
        this.byTrades = false;
        this.ProjectName = 'Volume Weighted Average Price';
        this.Comments = 'Weighted Moving Average where Volume is used as weights';
        this.SetIndicatorLine('Line', Color.Orange2, 3, LineStyle.SimpleChart);
        this.SeparateWindow = false;
        // #97150
        this.HideLineTimeShift = true;
        super.InputParameter(new InputParameterInteger('DaysCount', 'VWAP.DaysNumber', 0, 1, 366));
        super.InputParameter(new InputParameterRangeControl('TimeInterval', 'VWAP.StartEndDayTime', 1));
    }
    Init() {
        super.Init();
        this.calculator = new VWAPCalculator(this.DaysCount, this.TimeInterval);
    }
    GetIndicatorShortName() {
        return `VWAP(${this.DaysCount})`;
    }
    RefreshPromise() {
        const _super = Object.create(null, {
            RefreshPromise: { get: () => super.RefreshPromise }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.RefreshPromise.call(this);
            this.calculator.updateParameters(this.DaysCount, this.TimeInterval);
            this.vwapHistoryStatus = VWAPHistoryStatus.None;
            if (this.calculator.IsValidTimeFrame(this.CurrentData)) {
                this.vwapHistoricalRequest = new HistoricalDataRequest({
                    Instrument: this.CurrentData.Instrument,
                    DataType: this.CurrentData.DataType,
                    Period: Period.Min,
                    PeriodValue: 1
                });
            }
            else {
                this.vwapHistoryStatus = VWAPHistoryStatus.Invalid;
            }
            this.byTrades = this.CurrentData.DataType === IndicatorDataType.Trade;
        });
    }
    OnQuote() {
        super.OnQuote();
        switch (this.vwapHistoryStatus) {
            case VWAPHistoryStatus.Invalid:
                break;
            case VWAPHistoryStatus.None:
                void this.LoadVWAPHistory();
                break;
            case VWAPHistoryStatus.Processed: {
                const dateTime = DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(new Date());
                const volume = this.GetLastSize();
                const price = this.GetLastPrice();
                this.UpdateVWAPLine(dateTime, volume, price, 0);
                break;
            }
        }
    }
    getMinBarsCount() {
        var _a, _b;
        const period = (_b = (_a = this.CurrentData) === null || _a === void 0 ? void 0 : _a.Period) !== null && _b !== void 0 ? _b : Periods.MIN;
        const minutesInDay = Periods.DAY;
        return Math.max(1, Math.ceil((this.DaysCount * minutesInDay) / period));
    }
    LoadVWAPHistory() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.CurrentData != null && this.CurrentData.Count > 0) {
                this.vwapHistoryStatus = VWAPHistoryStatus.Loading;
                const interval = this.getCorrectTimePeriod();
                if (!isNullOrUndefined(interval)) {
                    this.vwapHistoricalRequest.fromUTC = interval.From;
                    this.vwapHistoricalRequest.toUTC = interval.To;
                    const historicalData = yield this.GetHistoricalDataPromise(this.vwapHistoricalRequest);
                    this.calculator.cleanInterval();
                    this.ProcessVWAPHistory(historicalData);
                }
                else {
                    this.vwapHistoryStatus = VWAPHistoryStatus.Invalid;
                }
            }
        });
    }
    ProcessVWAPHistory(historicalData) {
        var _a;
        if (historicalData instanceof BarData) {
            for (let i = historicalData.Count - 1; i >= 0; i--) {
                const volume = historicalData.Volume(i);
                const price = historicalData.getPriceByType(PriceType.Typical, i);
                const intervalDateTime = historicalData.Time(i);
                let offset = this.CurrentData.FindInterval(intervalDateTime.getTime());
                if (offset === -1) {
                    continue;
                }
                const barTime = DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(intervalDateTime);
                const vector = (_a = this.Data) === null || _a === void 0 ? void 0 : _a.get(0);
                offset = vector.Length - offset - 1;
                this.UpdateVWAPLine(barTime, volume, price, offset);
            }
        }
        this.vwapHistoryStatus = VWAPHistoryStatus.Processed;
    }
    UpdateVWAPLine(dateTime, volume, price, offset) {
        this.calculator.CalculateVWAP(dateTime, volume, price);
        if (this.calculator.AllowSetVWAP(dateTime)) {
            this.SetValue(0, offset, this.calculator.LastVWAPValue);
        }
    }
    GetLastPrice() {
        const level1 = this.CurrentData.Instrument.Level1;
        return level1.GetLastPriceByDefaultChartHistoryType();
    }
    GetLastSize() {
        if (!this.byTrades) {
            return 1;
        }
        const level1 = this.CurrentData.Instrument.Level1;
        return level1.GetLastSize(false);
    }
    getCorrectTimePeriod() {
        const firstBarOpenTime = new Date(this.CurrentData.FirstBarOpenTime);
        if (firstBarOpenTime.getHours() * 60 + firstBarOpenTime.getMinutes() > this.TimeInterval.getStartInMins()) {
            firstBarOpenTime.setDate(firstBarOpenTime.getDate() + 1);
        }
        firstBarOpenTime.setHours(0, 0, 0, 0);
        const to = new Date(this.CurrentData.getToTimeForRequest());
        to.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() + 1);
        const intervalInDays = Math.floor((to.getTime() - firstBarOpenTime.getTime()) / TimeSpan.FromDays(1).getTime());
        const coff = Math.floor(intervalInDays / this.DaysCount);
        const cashItem = this.CurrentData.cashItem;
        if (coff < 1 && !cashItem.isMaxHistoryLoaded) {
            return null;
        }
        const possibleDays = coff * this.DaysCount;
        let from = null;
        if (cashItem.isMaxHistoryLoaded) {
            from = new Date(this.CurrentData.FirstBarOpenTime);
        }
        else {
            from = new Date(to);
            from.setDate(to.getDate() - possibleDays);
            from.setHours(this.TimeInterval.startHours, this.TimeInterval.startMins);
        }
        return new Interval(from, to);
    }
    setProperties(props) {
        const needRecalculate = super.setProperties(props);
        if (!(this.TimeInterval instanceof TimeInterval)) {
            this.TimeInterval = new TimeInterval(this.TimeInterval.startHours, this.TimeInterval.startMins, this.TimeInterval.endHours, this.TimeInterval.endMins);
        }
        // let dp = DynProperty.getPropertyByName(props, 'VWAP.StartEndDayTime');
        // if (!isNullOrUndefined(dp)) {
        //     needRecalculate = this.TimeInterval.tryUpdate(dp.value);
        // }
        // dp = DynProperty.getPropertyByName(props, 'VWAP.DaysNumber');
        // if (this.DaysCount !== dp?.value) {
        //     this.DaysCount = dp.value;
        //     needRecalculate = true;
        // }
        return needRecalculate;
    }
}
